import { useEffect, useState } from "react";
import {
    Box,
    Button,
    Chip,
    Drawer,
    FormControl,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    Slider,
    Stack,
    TextField,
    Typography,
    useMediaQuery,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';
import CloseIcon from '@mui/icons-material/Close';
import SelectTopics from '../SelectTopics';
import { mascaraData, validarData } from '../../global/dataDigitada';

// filters.datetime guarda 'aaaa-mm-dd', 'aaaa-mm-ddThh:mm' ou 'Thh:mm' (so hora)
const partesDoFiltro = datetime => {
    const [dataISO = '', hora = ''] = String(datetime || '').split('T');
    return { dataISO, hora };
};

const dataISOparaTexto = dataISO =>
    /^\d{4}-\d{2}-\d{2}$/.test(dataISO) ? dataISO.replace(/^(\d{4})-(\d{2})-(\d{2})$/, '$3/$2/$1') : '';

// Montado a partir das partes: new Date('2026-10-12') e lido como UTC e, no
// fuso de Brasilia, mostraria o dia anterior
const rotuloDataHora = datetime => {
    const { dataISO, hora } = partesDoFiltro(datetime);
    const dataTexto = dataISOparaTexto(dataISO);

    if (dataTexto && hora) return `Data: ${dataTexto} ${hora}`;
    if (dataTexto) return `Data: ${dataTexto}`;
    return `Hora: ${hora}`;
};

const mascaraHora = texto => {
    const digitos = texto.replace(/\D/g, '').slice(0, 4);
    return digitos.length >= 3 ? `${digitos.slice(0, 2)}:${digitos.slice(2)}` : digitos;
};

const SearchField = ({ inputValue, inputChange, search, filters, setFilters, onClear }) => {
    const [drawerOpen, setDrawerOpen] = useState(false);
    const isMobile = useMediaQuery('(max-width:600px)');

    // O texto digitado vive aqui: publicar no filtro so quando a data ou a hora
    // estao completas, mas ler o valor de la, deixava o campo apagar tudo a cada
    // tecla — era impossivel digitar
    const [dataTexto, setDataTexto] = useState(() => dataISOparaTexto(partesDoFiltro(filters.datetime).dataISO));
    const [horaTexto, setHoraTexto] = useState(() => partesDoFiltro(filters.datetime).hora);

    // Reflete mudancas vindas de fora (chip removido, "limpar filtros")
    useEffect(() => {
        const { dataISO, hora } = partesDoFiltro(filters.datetime);
        setDataTexto(atual => (mascaraData(atual) === atual && !dataISO ? '' : dataISOparaTexto(dataISO)));
        setHoraTexto(hora);
    }, [filters.datetime]);

    const publicarDataHora = (textoData, textoHora) => {
        const partes = textoData.split('/');
        // Data impossivel (31/02, ano fora da faixa) nao vira filtro
        const dataCompleta = validarData(textoData).valida && partes.length === 3;
        const horaCompleta = /^\d{2}:\d{2}$/.test(textoHora);

        if (!dataCompleta && !horaCompleta) {
            setFilters(prev => {
                const { datetime, ...resto } = prev;
                return resto;
            });
            return;
        }

        const dataISO = dataCompleta ? `${partes[2]}-${partes[1]}-${partes[0]}` : '';
        setFilters(prev => ({
            ...prev,
            datetime: horaCompleta ? `${dataISO}T${textoHora}` : dataISO,
        }));
    };

    // So reclama depois que a data esta completa: nao adianta acusar erro no
    // meio da digitacao
    const erroData = dataTexto.replace(/\D/g, '').length === 8 ? validarData(dataTexto).erro : '';

    const hasActiveFilters = Object.entries(filters).some(([key, value]) => {
        if (key === 'price_range') return value && (value[0] > 0 || value[1] < 500);
        if (Array.isArray(value)) return value.length > 0;
        return value !== undefined && value !== null && value !== '';
    });

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') search();
    };

    const handleClearSearch = () => {
        inputChange('');
        onClear?.();
    };

    const applyFilters = () => {
        setDrawerOpen(false);
        search();
    };

    const clearFilters = () => {
        setFilters({});
        inputChange('');
        onClear?.();
    };

    return (
        <>
            {/* Barra de busca + botao filtro */}
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: 700, maxWidth: '100%' }}>
                <TextField
                    fullWidth
                    placeholder="Buscar por nome do encontro ou anfitrião..."
                    value={inputValue}
                    onChange={(e) => {
                        const val = e.target.value;
                        inputChange(val);
                        if (!val.trim()) onClear?.();
                    }}
                    onKeyDown={handleKeyDown}
                    size="small"
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '12px',
                            bgcolor: 'white',
                        },
                    }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ color: '#999' }} />
                            </InputAdornment>
                        ),
                        endAdornment: (
                            <InputAdornment position="end">
                                {inputValue && (
                                    <IconButton size="small" onClick={handleClearSearch} sx={{ mr: 0.5 }}>
                                        <CloseIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                )}
                                <Button
                                    onClick={search}
                                    variant="contained"
                                    size="small"
                                    sx={{
                                        bgcolor: '#006a61',
                                        borderRadius: '8px',
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        minWidth: isMobile ? 40 : 80,
                                        '&:hover': { bgcolor: '#005a52' },
                                    }}
                                >
                                    {isMobile ? <SearchIcon fontSize="small" /> : 'Buscar'}
                                </Button>
                            </InputAdornment>
                        ),
                    }}
                />
                <Button
                    onClick={() => setDrawerOpen(true)}
                    variant="outlined"
                    startIcon={<TuneIcon />}
                    sx={{
                        borderRadius: '12px',
                        textTransform: 'none',
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                        minWidth: 100,
                        height: 40,
                        borderColor: hasActiveFilters ? '#006a61' : '#ccc',
                        color: hasActiveFilters ? '#006a61' : '#666',
                        bgcolor: hasActiveFilters ? '#e6f3f1' : 'transparent',
                        '&:hover': { borderColor: '#006a61', bgcolor: '#e6f3f1' },
                    }}
                >
                    Filtro
                </Button>
            </Stack>
            <Box sx={{ width: 700, maxWidth: '100%' }}>

                {/* Chips de filtros ativos */}
                {hasActiveFilters && (
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                        {filters.datetime && (
                            <Chip
                                label={rotuloDataHora(filters.datetime)}
                                size="small"
                                onDelete={() => setFilters(prev => { const { datetime, ...rest } = prev; return rest; })}
                                sx={{ fontSize: '0.7rem' }}
                            />
                        )}
                        {filters.raffle !== undefined && (
                            <Chip
                                label={filters.raffle === 'true' ? 'Sorteio' : 'Agendamento'}
                                size="small"
                                onDelete={() => setFilters(prev => { const { raffle, ...rest } = prev; return rest; })}
                                sx={{ fontSize: '0.7rem' }}
                            />
                        )}
                        {filters.visible_public !== undefined && (
                            <Chip
                                label={filters.visible_public === 'true' ? 'Publico' : 'Privado'}
                                size="small"
                                onDelete={() => setFilters(prev => { const { visible_public, ...rest } = prev; return rest; })}
                                sx={{ fontSize: '0.7rem' }}
                            />
                        )}
                        {filters.price_range && (filters.price_range[0] > 0 || filters.price_range[1] < 500) && (
                            <Chip
                                label={`R$ ${filters.price_range[0]} - R$ ${filters.price_range[1]}`}
                                size="small"
                                onDelete={() => setFilters(prev => { const { price_range, ...rest } = prev; return rest; })}
                                sx={{ fontSize: '0.7rem' }}
                            />
                        )}
                        {filters.topics?.length > 0 && (
                            <Chip
                                label={`${filters.topics.length} topico${filters.topics.length > 1 ? 's' : ''}`}
                                size="small"
                                onDelete={() => setFilters(prev => { const { topics, ...rest } = prev; return rest; })}
                                sx={{ fontSize: '0.7rem' }}
                            />
                        )}
                        <Chip
                            label="Limpar tudo"
                            size="small"
                            onClick={clearFilters}
                            sx={{ fontSize: '0.7rem', color: '#c62828', borderColor: '#c62828' }}
                            variant="outlined"
                        />
                    </Stack>
                )}
            </Box>

            {/* Drawer de filtros */}
            <Drawer
                anchor="right"
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                PaperProps={{ sx: { width: isMobile ? '100%' : 380, p: 3 } }}
            >
                <Stack spacing={3} sx={{ height: '100%' }}>
                    {/* Header */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6" fontWeight={600}>Filtros</Typography>
                        <IconButton onClick={() => setDrawerOpen(false)}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    {/* Data e hora */}
                    <Box>
                        <Typography variant="body2" fontWeight={600} sx={{ mb: 1, color: '#333' }}>
                            Data e horário
                        </Typography>
                        <Stack direction="row" spacing={1}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Data"
                                placeholder="dd/mm/aaaa"
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ inputMode: 'numeric', maxLength: 10 }}
                                value={dataTexto}
                                onChange={e => {
                                    const texto = mascaraData(e.target.value);
                                    setDataTexto(texto);
                                    publicarDataHora(texto, horaTexto);
                                }}
                                error={Boolean(erroData)}
                                helperText={erroData}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                            />
                            <TextField
                                fullWidth
                                size="small"
                                label="Hora"
                                placeholder="hh:mm"
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ inputMode: 'numeric', maxLength: 5 }}
                                value={horaTexto}
                                onChange={e => {
                                    const texto = mascaraHora(e.target.value);
                                    setHoraTexto(texto);
                                    publicarDataHora(dataTexto, texto);
                                }}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                            />
                        </Stack>
                    </Box>

                    {/* Modalidade */}
                    <Box>
                        <Typography variant="body2" fontWeight={600} sx={{ mb: 1, color: '#333' }}>
                            Modalidade
                        </Typography>
                        <Stack direction="row" spacing={1}>
                            {[
                                { label: 'Todos', value: undefined },
                                { label: 'Sorteio', value: 'true' },
                                { label: 'Agendamento', value: 'false' },
                            ].map((opt) => (
                                <Chip
                                    key={opt.label}
                                    label={opt.label}
                                    onClick={() => setFilters(prev => {
                                        if (opt.value === undefined) {
                                            const { raffle, ...rest } = prev;
                                            return rest;
                                        }
                                        return { ...prev, raffle: opt.value };
                                    })}
                                    sx={{
                                        fontWeight: 500,
                                        bgcolor: filters.raffle === opt.value || (opt.value === undefined && filters.raffle === undefined) ? '#006a61' : '#f5f5f5',
                                        color: filters.raffle === opt.value || (opt.value === undefined && filters.raffle === undefined) ? 'white' : '#333',
                                        '&:hover': { opacity: 0.85 },
                                    }}
                                />
                            ))}
                        </Stack>
                    </Box>

                    {/* Visibilidade */}
                    <Box>
                        <Typography variant="body2" fontWeight={600} sx={{ mb: 1, color: '#333' }}>
                            Visibilidade
                        </Typography>
                        <Stack direction="row" spacing={1}>
                            {[
                                { label: 'Todos', value: undefined },
                                { label: 'Público', value: 'true' },
                                { label: 'Privado', value: 'false' },
                            ].map((opt) => (
                                <Chip
                                    key={opt.label}
                                    label={opt.label}
                                    onClick={() => setFilters(prev => {
                                        if (opt.value === undefined) {
                                            const { visible_public, ...rest } = prev;
                                            return rest;
                                        }
                                        return { ...prev, visible_public: opt.value };
                                    })}
                                    sx={{
                                        fontWeight: 500,
                                        bgcolor: filters.visible_public === opt.value || (opt.value === undefined && filters.visible_public === undefined) ? '#006a61' : '#f5f5f5',
                                        color: filters.visible_public === opt.value || (opt.value === undefined && filters.visible_public === undefined) ? 'white' : '#333',
                                        '&:hover': { opacity: 0.85 },
                                    }}
                                />
                            ))}
                        </Stack>
                    </Box>

                    {/* Range de valor */}
                    <Box>
                        <Typography variant="body2" fontWeight={600} sx={{ mb: 1, color: '#333' }}>
                            Faixa de valor
                        </Typography>
                        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                            <TextField
                                size="small"
                                value={(filters.price_range || [0, 500])[0] || ''}
                                onChange={(e) => {
                                    const raw = e.target.value.replace(/\D/g, '');
                                    const val = raw === '' ? 0 : Number(raw);
                                    setFilters(prev => ({ ...prev, price_range: [val, (prev.price_range || [0, 500])[1]] }));
                                }}
                                placeholder="0"
                                InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
                                sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                                inputProps={{ inputMode: 'numeric' }}
                            />
                            <Typography variant="body2" color="text.secondary">até</Typography>
                            <TextField
                                size="small"
                                value={(filters.price_range || [0, 500])[1] || ''}
                                onChange={(e) => {
                                    const raw = e.target.value.replace(/\D/g, '');
                                    const val = raw === '' ? 0 : Number(raw);
                                    setFilters(prev => ({ ...prev, price_range: [(prev.price_range || [0, 500])[0], val] }));
                                }}
                                placeholder="500"
                                InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
                                sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                                inputProps={{ inputMode: 'numeric' }}
                            />
                        </Stack>
                        <Slider
                            value={filters.price_range || [0, 500]}
                            onChange={(_, val) => setFilters(prev => ({ ...prev, price_range: val }))}
                            valueLabelDisplay="off"
                            min={0}
                            max={500}
                            step={10}
                            sx={{
                                color: '#006a61',
                                mx: 0.5,
                            }}
                        />
                    </Box>

                    {/* Topicos */}
                    <Box>
                        <Typography variant="body2" fontWeight={600} sx={{ mb: 1, color: '#333' }}>
                            Tópicos
                        </Typography>
                        <SelectTopics
                            onChange={(_, val) => setFilters(prev => ({ ...prev, topics: val }))}
                            name="topics"
                            value={filters.topics || []}
                            size="small"
                        />
                    </Box>

                    {/* Botoes */}
                    <Box sx={{ mt: 'auto', pt: 2, borderTop: '1px solid #eee' }}>
                        <Stack spacing={1.5}>
                            <Button
                                variant="contained"
                                fullWidth
                                onClick={applyFilters}
                                sx={{
                                    bgcolor: '#006a61',
                                    borderRadius: '8px',
                                    height: 44,
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    '&:hover': { bgcolor: '#005a52' },
                                }}
                            >
                                Aplicar filtros
                            </Button>
                            {hasActiveFilters && (
                                <Button
                                    variant="text"
                                    fullWidth
                                    onClick={() => { clearFilters(); setDrawerOpen(false); }}
                                    sx={{ textTransform: 'none', color: '#999' }}
                                >
                                    Limpar filtros
                                </Button>
                            )}
                        </Stack>
                    </Box>
                </Stack>
            </Drawer>
        </>
    );
};

export default SearchField;
